/**
 * /agents/agents — manage + create agent instances. Table of all agents with
 * enable toggle and a "New agent" modal that picks a type from the registry
 * (listTypes) and seeds its default config.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import { callAgents } from '../../hooks/useAgents';

export default function AgentsListPage() {
  const [agents, setAgents] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: '', name: '', scheduleMinutes: 15 });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/agents$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, typeRes] = await Promise.all([callAgents('listAgents'), callAgents('listTypes')]);
      setAgents(list.agents || []);
      setTypes(typeRes.types || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm({ type: types[0]?.type || '', name: '', scheduleMinutes: 15 });
    onOpen();
  };

  const create = async () => {
    if (!form.type || !form.name) {
      toast({ title: 'Type and name are required', status: 'warning', duration: 3000 });
      return;
    }
    setSaving(true);
    try {
      const res = await callAgents('createAgent', {
        agent: { type: form.type, name: form.name, scheduleMinutes: Number(form.scheduleMinutes) || 15 },
      });
      toast({ title: 'Agent created', status: 'success', duration: 2500 });
      onClose();
      history.push(`${sectionUrl}/agents/${res.agent.id}`);
    } catch (err) {
      toast({ title: 'Create failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (agent, enabled) => {
    try {
      await callAgents('toggleAgent', { id: agent.id, enabled });
      await load();
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={5} flexWrap="wrap" spacing={3}>
        <Box>
          <Heading size="md">Manage Agents</Heading>
          <Text fontSize="sm" color={subtle}>Create and configure always-on agents.</Text>
        </Box>
        <HStack>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>New agent</Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading && !agents.length ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Name</Th><Th>Type</Th><Th>Schedule</Th><Th>Mode</Th><Th>Enabled</Th></Tr>
            </Thead>
            <Tbody>
              {agents.map((agent) => (
                <Tr key={agent.id} _hover={{ bg: cardBorder, cursor: 'pointer' }} onClick={() => history.push(`${sectionUrl}/agents/${agent.id}`)}>
                  <Td fontWeight="medium">{agent.name}</Td>
                  <Td fontSize="xs">{agent.type}</Td>
                  <Td fontSize="xs">every {agent.schedule_minutes}m</Td>
                  <Td fontSize="xs">{agent.config?.actionMode || '—'}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Switch size="sm" isChecked={agent.enabled} colorScheme="green" onChange={(e) => toggle(agent, e.target.checked)} />
                  </Td>
                </Tr>
              ))}
              {!agents.length && (
                <Tr><Td colSpan={5}><Center py={10}><Text fontSize="sm" color={subtle}>No agents yet.</Text></Center></Td></Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New agent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Type</FormLabel>
                <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                  {types.map((t) => (
                    <option key={t.type} value={t.type}>{t.label}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color={subtle} mt={1}>
                  {types.find((t) => t.type === form.type)?.description}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Phishing & Spam Monitor" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Run every (minutes)</FormLabel>
                <NumberInput min={5} value={form.scheduleMinutes} onChange={(_, n) => setForm((f) => ({ ...f, scheduleMinutes: n || 15 }))}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={create} isLoading={saving}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
